import './RouteTable.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { useContext, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BiSolidDuplicate } from 'react-icons/bi';
import { MdDelete, MdEdit, MdPlayArrow, MdRule } from 'react-icons/md';
import { Plover, WtoDomain } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { Button } from '@Components/Button/Button';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { Appearance, Background, Design, Size } from '@src/Components/designConstants';
import { CopyButton } from '@Components/CopyButton';
import { ActionsMenu, ActionsMenuItem } from '../ActionsMenu/ActionsMenu';
import { WtoTab, WtoTabType } from '../web-traffic-optimization-constants';
import { FormContext } from '../wto-form-context';
import { joinBackslashes } from '../wto-utils';
import { RouteStatus } from './RouteStatus';

const INNER_CELL_HEIGHT = 70;

const FILTER_OPTIONS = ['All', 'Live', 'Draft', 'Archive'] as const;
const DATE_FORMAT = 'EEE, LLL dd, yyyy, hh:mm aa';

interface RouteTableProps {
  data: Plover[];
  setModalData: (modalType: string, data: Plover) => void;
  goToTab: (tab: WtoTabType) => void;
  loading: boolean;
  domains: WtoDomain[];
}

const renderStatus = (status: string, isDeleted: boolean) => {
  switch (status.toLocaleLowerCase()) {
    case 'production_deployment_failed':
      return <RouteStatus color={['#ed4924']} status={['Error']} />;
    case 'production':
      return isDeleted ? (
        <RouteStatus color={['#efc63d']} status={['Archived']} />
      ) : (
        <RouteStatus color={['#46b881']} status={['Live']} />
      );
    case 'draft':
      return isDeleted ? (
        <RouteStatus color={['#efc63d']} status={['Archived']} />
      ) : (
        <RouteStatus color={['#9e9e9e']} status={['Draft']} />
      );
    default:
      return <RouteStatus color={['#fc9827']} status={['Unknown']} />;
  }
};

const NoRowsMessage = ({ hasDomains }: { hasDomains: boolean }) => (
  <div className='no-configurations' data-testid='no-configurations'>
    {hasDomains ? 'No data to show' : 'Domains not configured for this partner'}
  </div>
);

const renderNoRows = (hasDomains: boolean) =>
  function NoRowsRenderer() {
    return <NoRowsMessage hasDomains={hasDomains} />;
  };

const buildPloverPreviewUrl = (row: Plover) => {
  const path = row.folder_path ? `/${row.folder_path}/` : '/';
  return `https://${row.domain || ''}${path}${row.plover_url || ''}`;
};

export const RouteTable = ({ data, setModalData, goToTab, loading, domains }: RouteTableProps) => {
  const [statusFilter, setStatusFilter] = useState<(typeof FILTER_OPTIONS)[number]>('All');
  const { setWtoFormObj } = useContext(FormContext);

  const rowActionsMenuItems = (row: Plover): ActionsMenuItem[] => {
    const status = row.status.toLocaleLowerCase();
    const canDuplicateOrActivate =
      (status === 'production' || status === 'draft') && !row.is_deleted;
    return [
      {
        key: 'details',
        label: 'Details',
        icon: <MdEdit />,
        onClick: () => {
          setWtoFormObj({ ...row, folder_path: joinBackslashes(row.folder_path) });
          goToTab(WtoTab.ROUTE_DETAILS);
        }
      },
      {
        key: 'rules',
        label: 'Rules',
        icon: <MdRule />,
        onClick: () => {
          setWtoFormObj({ ...row, folder_path: joinBackslashes(row.folder_path) });
          goToTab(WtoTab.ROUTE_RULES);
        }
      },
      ...(canDuplicateOrActivate
        ? [
            {
              key: 'duplicate',
              label: 'Duplicate',
              icon: <BiSolidDuplicate />,
              onClick: () => setModalData('Duplicate Route', row)
            },
            ...(status === 'draft'
              ? [
                  {
                    key: 'activate',
                    label: 'Activate Route',
                    icon: <MdPlayArrow />,
                    onClick: () => setModalData('Activate Route', row)
                  }
                ]
              : []),
            {
              key: 'archive',
              label: 'Archive',
              icon: <MdDelete />,
              onClick: () => setModalData('Archive Route', row)
            }
          ]
        : [])
    ];
  };

  const filterData = (rows: Plover[]) => {
    const filter = statusFilter.toLocaleLowerCase();
    return rows.filter(row => {
      const status = row.status.toLocaleLowerCase();
      return (
        (row.is_deleted && filter === 'archive') ||
        (!row.is_deleted &&
          (filter === 'all' ||
            (filter === 'draft' && status === 'draft') ||
            (filter === 'live' && status === 'production')))
      );
    });
  };

  const headers = [
    {
      label: 'Route Name',
      name: 'test_name',
      type: 'text',
      flex: true,
      minFlexWidth: 150
    },
    {
      label: 'Tracking Parameter',
      name: 'utm_test_id',
      type: 'text',
      flex: true,
      minFlexWidth: 250
    },
    {
      label: 'Plover Name',
      name: 'plover_url',
      type: 'text',
      flex: true,
      minFlexWidth: 150,
      renderer: (row: Plover) => (
        <span>
          {row.plover_url}
          <CopyButton text={buildPloverPreviewUrl(row)} tooltipLabel='Copy URL to Clipboard' />
        </span>
      )
    },
    {
      label: 'Last Modified',
      name: 'updated_at',
      type: 'text',
      sortPriority: 1,
      flex: true,
      minFlexWidth: 250,
      renderer: (row: Plover) =>
        row.updated_at ? Dfns.format(DATE_FORMAT, new Date(row.updated_at)) : ''
    },
    {
      label: 'Status',
      name: 'dri',
      type: 'text',
      width: 120,
      nonInteractive: true,
      renderer: (row: Plover) => (
        <OverlayTrigger
          placement='bottom'
          overlay={<Tooltip id='id:go to route details'>Go to Route Details</Tooltip>}
        >
          <InteractiveDiv
            onClick={() => {
              setWtoFormObj({
                ...row,
                folder_path: joinBackslashes(row.folder_path)
              });
              goToTab(WtoTab.ROUTE_DETAILS);
            }}
          >
            {renderStatus(row.status, row.is_deleted)}
          </InteractiveDiv>
        </OverlayTrigger>
      )
    },
    {
      label: 'Actions',
      width: 90,
      nonInteractive: true,
      renderer: (row: Plover) => (
        <ActionsMenu
          items={rowActionsMenuItems(row)}
          popoverId={`row-actions-${row.id}`}
          menuTestId='row-actions-menu'
          toggleTestId={`row-actions-${row.id}`}
        />
      )
    }
  ];

  return (
    <div className='route-table' data-testid='route-table'>
      <div className='status-filter-buttons'>
        {FILTER_OPTIONS.map(status => (
          <Button
            size={Size.LG}
            key={status}
            className={cn('status-filter-button', { selected: status === statusFilter })}
            onClick={() => setStatusFilter(status)}
            appearance={Appearance.EMPTY}
            design={Design.BRAND}
            background={Background.LIGHT}
          >
            {status}
          </Button>
        ))}
      </div>
      {loading ? (
        <FullPageSpinner />
      ) : (
        <BPMTable
          data={filterData(data)}
          headers={headers}
          noRowsRenderer={renderNoRows(domains.length > 0)}
          rowHeight={INNER_CELL_HEIGHT}
          pagination
        />
      )}
    </div>
  );
};
